import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CartAddEntrySuccessEvent, CartModification } from '@spartacus/cart/base/root';
import { EventService, TranslationService } from '@spartacus/core';
import { filter, switchMap, map, take } from 'rxjs/operators';

import { importToolMapCodesConstant } from '@app/core/constants/import-tool-map-codes.constant';
import { DpMessageService } from '@app/core/services/message.service';
import { DpCart } from '@app/core/types/view/dp-cart.interface';
import { DpCartService } from '@app-modules/cart/shared';

@Injectable({ providedIn: 'root' })
export class DpCartNotificationService {
  readonly #cartService = inject(DpCartService);
  readonly #eventService = inject(EventService);
  readonly #messageService = inject(DpMessageService);
  readonly #translationService = inject(TranslationService);

  constructor() {
    this.#eventService
      .get(CartAddEntrySuccessEvent)
      .pipe(
        takeUntilDestroyed(),
        switchMap((addedEntry: CartAddEntrySuccessEvent) =>
          this.#cartService.getActive().pipe(
            take(1),
            map((cart) => this.#getStockMessageKey(addedEntry, cart)),
            filter(Boolean),
            switchMap((key) =>
              this.#translationService.translate(key).pipe(
                take(1),
                map(
                  (translation) =>
                    `${addedEntry?.entry?.product?.name ?? addedEntry?.entry?.product?.code}: ${translation}`
                )
              )
            )
          )
        )
      )
      .subscribe((message: string) => {
        this.#messageService.removeError();
        this.#messageService.showError(message, 5000);
      });
  }

  #getStockMessageKey(addedEntry: CartModification, cart: DpCart): string | undefined {
    if (importToolMapCodesConstant.has(addedEntry.statusCode)) {
      return importToolMapCodesConstant.get(addedEntry.statusCode);
    } else if (this.#isStockValid(addedEntry, cart)) {
      return this.#getMessageKeyByStock(addedEntry.entry?.defaultWarehouseStockAmount);
    }
  }

  #isStockValid(addedEntry: CartModification, cart: DpCart): boolean {
    const quantity = (cart.entries ?? [])
      .filter((entry) => !!entry.product && entry.product.code === addedEntry?.productCode)
      .map((entry) => entry.quantity ?? 0)
      .reduce((value, acc) => value + acc, addedEntry?.quantityAdded ?? 0);

    return (
      (!!addedEntry?.entry?.defaultWarehouseStockAmount && quantity > addedEntry?.entry?.defaultWarehouseStockAmount) ||
      addedEntry?.entry?.defaultWarehouseStockAmount === 0
    );
  }

  #getMessageKeyByStock(stockAmout = 0): string {
    return stockAmout === 0 ? 'dpInfoMessages.longerDelivery' : 'dpInfoMessages.low-qty';
  }
}
